import React, { useRef, useEffect, useState, useCallback } from 'react'
import ReactDOM from 'react-dom'
import styled from 'styled-components'
import PropTypes from 'prop-types'
import { isJson } from 'Components/Common'
import { inIframe } from 'Utils/helper'

const DialogContainer = styled.div`
  background-color: rgba(0, 0, 0, 0.5);
  width: 100%;
  height: 100vh;
  top: 0;
  left: 0;
  display: flex;
  position: fixed;
  z-index: 1000;
  align-items: ${() => (inIframe() ? 'flex-start' : 'center')};
  justify-content: center;
  padding: 20px;
  box-sizing: border-box;
`

const DialogBody = styled.div`
  box-sizing: border-box;
  position: relative;
  max-height: 90vh;
  transform: translateY(${props => (inIframe() ? '200px' : '0px')});
  border-radius: 12px;
  box-shadow: 0 20px 40px -4px rgba(0, 0, 0, 0.25),
    0 8px 32px -8px rgba(0, 0, 0, 0.15);
  background-color: ${props => props.theme.color.bg};

  @media (max-width: ${props => props.theme.breakpoint.md}px) {
    margin: 20px;
    max-height: calc(100vh - 40px);
  }
`

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${props => props.theme.color.primaryText};
  opacity: 0.6;
  transition: all 0.2s ease;

  &:hover {
    opacity: 1;
    background-color: rgba(0, 0, 0, 0.1);
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
  </svg>
)

const Dialog = ({
  isOpen,
  onClose,
  children,
  showCloseButton = true,
  closeOnClickOutside = true,
  closeOnEscape = true
}) => {
  const dialogRef = useRef(null)
  const dialogBodyRef = useRef(null)
  const [frameRect, setFrameRect] = useState(null)

  const handleClose = useCallback(() => {
    if (inIframe()) {
      window.parent.postMessage(
        JSON.stringify({
          type: 'DetachScrollListener',
          message: null
        }),
        localStorage.getItem('parentOrigin')
      )
      window.removeEventListener('message', receiveRect, false)
    }
    onClose()
  }, [onClose])

  const handleContainerClick = useCallback(
    e => {
      if (
        closeOnClickOutside &&
        dialogBodyRef.current &&
        !dialogBodyRef.current.contains(e.target)
      ) {
        handleClose()
      }
    },
    [closeOnClickOutside, handleClose]
  )

  const receiveRect = e => {
    const { type, message } = isJson(e.data) ? JSON.parse(e.data) : e.data
    if (type === 'SeeClientRect') {
      window.removeEventListener('message', receiveRect, false)
      setFrameRect(message.top)
    }
  }

  useEffect(() => {
    const handleKeyDown = e => {
      if (closeOnEscape && e.key === 'Escape') {
        handleClose()
      }
    }

    if (isOpen) {
      document.body.style.overflow = 'hidden'
      if (closeOnEscape) {
        document.addEventListener('keydown', handleKeyDown)
      }
    } else {
      document.body.style.overflow = 'unset'
    }

    return () => {
      document.body.style.overflow = 'unset'
      if (closeOnEscape) {
        document.removeEventListener('keydown', handleKeyDown)
      }
    }
  }, [isOpen, closeOnEscape, handleClose])

  useEffect(() => {
    if (dialogBodyRef.current !== null && frameRect < 0) {
      dialogBodyRef.current.style.transform = `translateY(${-(
        frameRect - 150
      )}px`
    }
    window.addEventListener('message', receiveRect, false)
  }, [isOpen, receiveRect, frameRect])

  useEffect(() => {
    //Emulate position: fixed in an iframe using transforms and position: absolute
    if (inIframe()) {
      window.removeEventListener('message', receiveRect, false)
      window.addEventListener('message', receiveRect, false)
      window.parent.postMessage(
        JSON.stringify({
          type: 'ReceiveClientRect',
          message: null
        }),
        localStorage.getItem('parentOrigin')
      )
    }
  }, [])

  if (!isOpen) return null

  return ReactDOM.createPortal(
    <DialogContainer ref={dialogRef} onClick={handleContainerClick}>
      <DialogBody ref={dialogBodyRef}>
        {showCloseButton && (
          <CloseButton onClick={handleClose}>
            <CloseIcon />
          </CloseButton>
        )}
        {children}
      </DialogBody>
    </DialogContainer>,
    document.querySelector('#modal') || document.body
  )
}

Dialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  children: PropTypes.node.isRequired,
  showCloseButton: PropTypes.bool,
  closeOnClickOutside: PropTypes.bool,
  closeOnEscape: PropTypes.bool
}

export default Dialog
